/**
 * The review step's behavior half: the price line and note the app's confirm
 * page draws (bookingRules' reviewPriceRow and approvalNote, shared with it),
 * and the booking itself on the website session, then the booked page. A
 * booking that did not go through is the app's failure (confirmBookingError,
 * row V7): Try Again books the same attempt again, Go Back steps back. Draws
 * nothing.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WebAppFlowContextResponse } from '@/api/types';
import type { ConfirmBookingResponse } from '@/api/booking';
import { mapConfirmBookingError, type ConfirmBookingError } from '@/hooks/confirmBookingError';
import { approvalNote, reviewPriceRow } from '@/pages/booking/shared/bookingRules';
import { getConfirmationView, getViewConfig } from '@/pages/ConfirmPage/getConfirmationView';
import { bookingFailureTitle } from '@/pages/ConfirmPage/ReviewStepScreen';

import { SiteFetchError, webFetch } from '../../api/session';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { flowKeys, newIdempotencyKey } from '../../hooks/useWebFlow';
import { usePageMover } from '../../pageMover';
import { contract, type ReviewStepActions, type ReviewStepEvents, type ReviewStepState } from './contract';

export interface ReviewStepInput extends PieceInput<ReviewStepEvents> {
  readonly flowToken: string;
  readonly flowContext: WebAppFlowContextResponse;
  /** Go Back from a booking that did not go through: one step back. */
  readonly onBack?: () => void;
  /** The checkout asks for the number to call (service-by-phone D5): the booking waits for it. */
  readonly waitingForNumber?: boolean;
}

export type ReviewStepRuntime = PieceRuntime<typeof contract, ReviewStepState, ReviewStepActions> & {
  /** The failure as the app's failure screen takes it (BookingFailureScreen); null when nothing went wrong. */
  readonly failure: ConfirmBookingError | null;
};

export function useReviewStep(input: ReviewStepInput): ReviewStepRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const queryClient = useQueryClient();
  const { flowToken, flowContext, onBack } = input;
  const waitingForNumber = input.waitingForNumber ?? false;
  const view = getViewConfig(getConfirmationView(flowContext));
  const request = view.view === 'REVIEW_APPROVAL';
  const service = flowContext.context.service;

  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<ConfirmBookingError | null>(null);
  // One key per attempt, kept for Try Again: a retry after a lost answer replays, never books twice.
  const keyRef = useRef<string | null>(null);

  const confirm = useCallback(() => {
    if (confirming || waitingForNumber) return;
    keyRef.current ??= newIdempotencyKey();
    setConfirming(true);
    setFailure(null);
    webFetch<ConfirmBookingResponse>('/booking', {
      method: 'POST',
      headers: { 'Idempotency-Key': keyRef.current },
      body: JSON.stringify({ flowToken }),
    })
      .then((booking) => {
        keyRef.current = null;
        emit('booked', { bookingId: booking.bookingId, status: booking.status });
        navigate(`/booked/${encodeURIComponent(booking.bookingId)}`, { replace: true });
      })
      .catch((e: unknown) => {
        setConfirming(false);
        if (e instanceof SiteFetchError && e.code === 'FORMS_OUTSTANDING') {
          // A form is still owed: the checkout returns to it.
          keyRef.current = null;
          void queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) });
          return;
        }
        setFailure(mapConfirmBookingError(e));
      });
  }, [confirming, waitingForNumber, flowToken, emit, navigate, queryClient]);

  const goBack = useCallback(() => {
    setFailure(null);
    keyRef.current = null;
    onBack?.();
  }, [onBack]);

  const actions = useMemo<ReviewStepActions>(() => ({ confirm, retry: confirm, goBack }), [confirm, goBack]);

  return {
    state: {
      request,
      price: reviewPriceRow(service, request),
      note: request ? approvalNote(service) : null,
      confirmLabel: view.buttonText,
      confirming,
      waitingForNumber,
      problem: failure?.message ?? null,
      failure: failure ? { title: bookingFailureTitle(failure), message: failure.message, canRetry: failure.canRetry } : null,
    },
    actions,
    options,
    failure,
  };
}
